"use client";

import Link from "next/link";
import { TbCar, TbCar4Wd, TbColorSwatch } from "react-icons/tb";
import { GiSteeringWheel } from "react-icons/gi";
import { LuCalendar, LuFuel } from "react-icons/lu";
import { FiCheck, FiTag } from "react-icons/fi";
import { FaHashtag, FaHorseHead, FaPhone, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";
import { MdWarning } from "react-icons/md";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";

import Calculator from "@components/ui/calculator";
import Lightbox from "@components/ui/lightbox";
import Button from "@components/ui/form/button";
import { formatCurrency } from "@utils/format";
import { DEFAULT_CASH_DEPOSIT, DEFAULT_INTEREST_RATE, DEFAULT_MONTHS, DEFAULT_SALVAGE_AMOUNT } from "@utils/constants";
import { formatMonthlyCost } from "@utils/format";
import { CarData } from "@src/types/car";
import { getFacilityBySlug } from "@/data/facilities";
import { GearboxIcon, OdometerIcon } from "../ui/icons";

/** Stagger animation wrapper */
const FadeIn = ({
	children,
	delay = 0,
	className = "",
}: {
	children: React.ReactNode;
	delay?: number;
	className?: string;
}) => {
	const ref = useRef(null);
	const isInView = useInView(ref, { once: true, margin: "-40px" });

	return (
		<motion.div
			ref={ref}
			className={className}
			initial={{ opacity: 0, y: 20 }}
			animate={isInView ? { opacity: 1, y: 0 } : {}}
			transition={{ duration: 0.5, delay, ease: [0.25, 0.4, 0.25, 1] }}
		>
			{children}
		</motion.div>
	);
};

/** Single spec item */
const SpecItem = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) => {
	if (!value) return null;
	return (
		<div className="flex items-start gap-3 p-4">
			<div className="text-brand mt-0.5 shrink-0">{icon}</div>
			<div className="min-w-0">
				<div className="text-base font-semibold text-white truncate">{value}</div>
				<div className="text-xs text-gray-500 mt-0.5">{label}</div>
			</div>
		</div>
	);
};

const CarInfo = ({ data }: { data: CarData }) => {
	if (!data) return;

	const facility = data.facilitySlug ? getFacilityBySlug(data.facilitySlug) : undefined;

	const specs = [
		...(facility ? [{ icon: <FaMapMarkerAlt size={18} />, label: "Plats", value: facility.name }] : []),
		...(!data.regNoHidden && data.regNo
			? [{ icon: <FaHashtag size={18} />, label: "Registreringsnummer", value: data.regNo }]
			: []),
		{ icon: <FiTag size={18} />, label: "Märke", value: data.make },
		{
			icon: <GiSteeringWheel size={18} />,
			label: "Modell",
			value: data.additionalVehicleData?.vehicleName ?? data.model,
		},
		{ icon: <LuCalendar size={18} />, label: "Årsmodell", value: data.modelYear },
		{ icon: <OdometerIcon size={18} />, label: "Mil", value: data.milage },
		{ icon: <GearboxIcon size={18} />, label: "Växellåda", value: data.gearBox },
		{ icon: <TbCar size={18} />, label: "Kaross", value: data.bodyType },
		...(data.vehicleType === "Car" &&
		(data.additionalVehicleData?.fourWheelDrive || data.additionalVehicleData?.driveWheels)
			? [
					{
						icon: <TbCar4Wd size={18} />,
						label: "Drivhjul",
						value: data.additionalVehicleData?.fourWheelDrive
							? "Fyrhjulsdriven"
							: data.additionalVehicleData?.driveWheels,
					},
				]
			: []),
		{ icon: <LuFuel size={18} />, label: "Bränsle", value: data.fuel },
		{ icon: <TbColorSwatch size={18} />, label: "Färg", value: data.freetextColor },
		{ icon: <FaHorseHead size={18} />, label: "Hästkrafter", value: data.additionalVehicleData?.engineEffectHp },
	];

	return (
		<div className="content-container px-4 2xl:px-0 pt-24 pb-8 lg:pb-16">
			{/* Mobile fixed CTA bar */}
			<div className="flex md:hidden fixed bottom-0 left-0 w-full bg-gradient-to-t from-black via-black/95 to-transparent px-4 pb-5 pt-8 gap-3 z-10">
				<Link className="w-full" href="tel:+461160501">
					<Button full>
						<FaPhone size={12} className="mr-2" /> Ring oss
					</Button>
				</Link>
				<Link className="w-full" href={`mailto:${facility?.email}`}>
					<Button full variant="accent">
						<FaEnvelope size={12} className="mr-2" /> Maila oss
					</Button>
				</Link>
			</div>

			{/* Main grid: Gallery + Sidebar */}
			<div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 w-full">
				{/* Gallery */}
				<div className="col-span-1 lg:col-span-2 space-y-4">
					<div className="relative rounded-xl overflow-hidden">
						<Lightbox
							images={data?.images.map((img) => ({
								src: img.imageFormats[0].url,
								height: 768,
								width: 1024,
								alt: data.name,
							}))}
						/>
					</div>

					{/* Mobile title + description */}
					<div className="py-5 block lg:hidden">
						<h1 className="text-2xl font-bold">{data.name}</h1>
						<p className="text-gray-400 mt-1" dangerouslySetInnerHTML={{ __html: data.description }} />
					</div>
					{/* Desktop title + description */}
					<FadeIn className="hidden lg:block mt-8">
						<h1 className="text-3xl font-bold">{data.name}</h1>
						<p className="text-gray-400 mt-2 max-w-3xl" dangerouslySetInnerHTML={{ __html: data.description }} />
					</FadeIn>

					{/* Specs grid */}
					<FadeIn className="col-span-1 lg:col-span-2" delay={0.1}>
						<div className="rounded-xl border border-white/[0.06] bg-white/[0.03]">
							<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 divide-x divide-y divide-white/[0.06]">
								{specs.map((spec) => (
									<SpecItem key={spec.label} {...spec} />
								))}
							</div>
						</div>
					</FadeIn>

					{/* Equipment */}
					<FadeIn className="col-span-1" delay={0.2}>
						<div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-5">
							<p className="text-sm text-gray-500 uppercase tracking-wide mb-4">Utrustning</p>
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
								{[...(data.equipment ?? [])]
									// .sort((a, b) => a.localeCompare(b, "sv"))
									.map((eq) => (
										<div key={eq} className="flex items-start gap-2 text-sm text-gray-300">
											<FiCheck className="text-accent mt-0.5 shrink-0" size={14} />
											<span>{eq}</span>
										</div>
									))}
							</div>
						</div>
					</FadeIn>
				</div>

				{/* Pricing sidebar */}
				<FadeIn className="col-span-1">
					<div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-6 lg:sticky lg:top-24">
						<div className="space-y-5">
							{/* Price */}
							<div>
								<p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Pris</p>
								<p className="text-3xl font-bold text-white">{formatCurrency(data.price.value)}</p>
								<p className="text-sm text-gray-400 mt-1">
									Från{" "}
									<span className="text-white font-medium">
										{formatMonthlyCost(
											data.price.value,
											DEFAULT_MONTHS,
											DEFAULT_INTEREST_RATE,
											DEFAULT_CASH_DEPOSIT,
											DEFAULT_SALVAGE_AMOUNT
										)}
									</span>
								</p>
							</div>

							<div className="h-px bg-white/[0.06]" />

							{/* Calculator */}
							<Calculator price={data.price.value} />

							<div className="h-px bg-white/[0.06]" />

							{/* Contact buttons */}
							<div className="hidden md:flex flex-col gap-2">
								<Link className="w-full" href="tel:+461160501">
									<Button full>
										<FaPhone size={12} className="mr-2" /> Ring oss
									</Button>
								</Link>
								<Link className="w-full" href={`mailto:${facility?.email}`}>
									<Button full variant="accent">
										<FaEnvelope size={12} className="mr-2" /> Maila oss
									</Button>
								</Link>
							</div>
						</div>
					</div>
				</FadeIn>
			</div>
			{/* Finance warning */}
			<FadeIn className="mt-4" delay={0.1}>
				<div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-5 flex items-start gap-4">
					<MdWarning className="shrink-0 text-amber-500 mt-0.5" size={24} />
					<div>
						<p className="font-semibold text-sm text-white mb-1">Att låna kostar pengar</p>
						<p className="text-xs text-gray-500 leading-relaxed">
							Om du inte kan betala tillbaka skulden i tid riskerar du en betalningsanmärkning. Det kan leda till
							svårigheter att få hyra bostad, teckna abonnemang och få nya lån. För stöd, vänd dig till budget- och
							skuldrådgivningen i din kommun. Kontaktuppgifter finns på{" "}
							<Link
								href="https://konsumentverket.se"
								target="_blank"
								className="underline hover:text-white transition-colors"
							>
								konsumentverket.se
							</Link>
							.
						</p>
					</div>
				</div>
			</FadeIn>
		</div>
	);
};

export default CarInfo;
